import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { QuestionThreadService } from 'utility/services/question-threads'
import {
    countThreads,
    normalizeReview,
    normalizeThreadsResponse,
    shouldHideQuestion,
    threadAnchorId,
    threadMapByQuestion,
    upsertThread,
} from 'utility/question-threads'

const EMPTY = {
    available: false,
    reviewFlow: 0,
    answerChangeAllowed: false,
    canComment: false,
    canReply: false,
    review: normalizeReview(null),
    threads: [],
}

// Without a provider (public inspection, template preview) every question renders exactly as before.
const QuestionThreadsContext = createContext({
    ...EMPTY,
    auditId: null,
    map: {},
    counts: { Open: 0, Replied: 0, Closed: 0 },
    filterOn: false,
    setFilterOn: () => {},
    isHidden: () => false,
    applyThread: () => {},
    applyReview: () => {},
    reload: () => {},
    answerChanged: () => {},
    focusedQuestionId: null,
})

export const useQuestionThreads = () => useContext(QuestionThreadsContext)

const FOCUS_TRIES = 25
const FOCUS_INTERVAL_MS = 400
const FOCUS_HIGHLIGHT_MS = 4000
// file links are signed for about an hour: reload the threads when the page comes back after a long pause
const STALE_LINKS_MS = 50 * 60 * 1000

/**
 * Loads the review threads of one inspection (GET Inspection/{auditId}/Threads) for the question cards below it.
 * auditId null / 0 = off. ReviewFlow 0, 404 or 503 (not installed) = nothing shown.
 * answerChangeEnabled: a reply may also change the answer only on a completed inspection shown read-only (no local
 * unsaved edits that the reload of the inspection would drop); the server refuses it on open inspections as well.
 * onAnswerChanged: called after a reply changed the question's answer (the page reloads the inspection).
 * Opening the page with location.state.focusQuestionId scrolls to that question's thread (inbox rows).
 */
export const QuestionThreadsProvider = ({ auditId, answerChangeEnabled = false, onAnswerChanged, children }) => {
    const location = useLocation()
    const [state, setState] = useState(EMPTY)
    const [filterOn, setFilterOn] = useState(false)
    const [focusedQuestionId, setFocusedQuestionId] = useState(null)
    const onAnswerChangedRef = useRef(onAnswerChanged)
    onAnswerChangedRef.current = onAnswerChanged
    const id = Number(auditId)
    const validId = Number.isInteger(id) && id > 0 ? id : null
    // only the latest request for the current audit may set the state
    const requestRef = useRef(0)
    const auditRef = useRef(validId)
    auditRef.current = validId
    const loadedAtRef = useRef(0)

    /** keepOnError: a failed reload keeps what is on screen (a failed first load of an audit shows nothing). */
    const fetchThreads = useCallback(
        keepOnError => {
            requestRef.current += 1
            const request = requestRef.current
            const forAudit = validId
            if (!forAudit) {
                setState(EMPTY)
                return Promise.resolve()
            }
            const isCurrent = () => request === requestRef.current && auditRef.current === forAudit
            return QuestionThreadService.list(forAudit)
                .then(response => {
                    if (!isCurrent()) return
                    loadedAtRef.current = Date.now()
                    setState(normalizeThreadsResponse(response))
                })
                .catch(() => {
                    // 503 QUESTION_COMMENTS_NOT_INSTALLED / 404 on an older backend / any failure
                    if (isCurrent() && !keepOnError) setState(EMPTY)
                })
        },
        [validId],
    )

    useEffect(() => {
        setState(EMPTY)
        setFilterOn(false)
        fetchThreads(false)
        return () => {
            // a response arriving after the audit changed / the page closed is ignored
            requestRef.current += 1
        }
    }, [fetchThreads])

    const reload = useCallback(() => fetchThreads(true), [fetchThreads])

    // signed file links expire: refresh when the tab is used again after a long pause
    useEffect(() => {
        if (!validId) return undefined
        const onBack = () => {
            if (document.visibilityState === 'hidden') return
            if (loadedAtRef.current && Date.now() - loadedAtRef.current > STALE_LINKS_MS) {
                // focus + visibilitychange fire together: start one refresh only
                loadedAtRef.current = Date.now()
                fetchThreads(true)
            }
        }
        window.addEventListener('focus', onBack)
        document.addEventListener('visibilitychange', onBack)
        return () => {
            window.removeEventListener('focus', onBack)
            document.removeEventListener('visibilitychange', onBack)
        }
    }, [validId, fetchThreads])

    // Inbox link: scroll to the question once its card is on the page (the inspection may still be loading).
    const focusQuestionId = Number(location.state?.focusQuestionId) || null
    useEffect(() => {
        if (!state.available || !focusQuestionId) return undefined
        let tries = 0
        let clearTimer = null
        const timer = setInterval(() => {
            tries += 1
            const el = document.getElementById(threadAnchorId(focusQuestionId))
            if (el) {
                clearInterval(timer)
                el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                setFocusedQuestionId(focusQuestionId)
                clearTimer = setTimeout(() => setFocusedQuestionId(null), FOCUS_HIGHLIGHT_MS)
            } else if (tries >= FOCUS_TRIES) {
                clearInterval(timer)
            }
        }, FOCUS_INTERVAL_MS)
        return () => {
            clearInterval(timer)
            if (clearTimer) clearTimeout(clearTimer)
        }
    }, [state.available, focusQuestionId])

    const applyThread = useCallback(thread => {
        if (!thread) return
        // a reload still in flight must not overwrite the thread that was just saved
        requestRef.current += 1
        setState(prev => ({ ...prev, threads: upsertThread(prev.threads, thread) }))
    }, [])

    /** "Review done" state returned by Review/Done or Review/Undo. */
    const applyReview = useCallback(review => {
        if (!review) return
        requestRef.current += 1
        setState(prev => ({ ...prev, review: normalizeReview(review) }))
    }, [])

    const answerChanged = useCallback(() => {
        if (typeof onAnswerChangedRef.current === 'function') onAnswerChangedRef.current()
    }, [])

    const map = useMemo(() => threadMapByQuestion(state.threads), [state.threads])
    const counts = useMemo(() => countThreads(state.threads), [state.threads])

    const isHidden = useCallback(
        (item, nestedItems) => state.available && shouldHideQuestion({ filterOn, item, nestedItems, map }),
        [state.available, filterOn, map],
    )

    const value = useMemo(
        () => ({
            ...state,
            answerChangeAllowed: state.answerChangeAllowed && !!answerChangeEnabled,
            auditId: validId,
            map,
            counts,
            filterOn,
            setFilterOn,
            isHidden,
            applyThread,
            applyReview,
            reload,
            answerChanged,
            focusedQuestionId,
        }),
        [
            state,
            answerChangeEnabled,
            validId,
            map,
            counts,
            filterOn,
            isHidden,
            applyThread,
            applyReview,
            reload,
            answerChanged,
            focusedQuestionId,
        ],
    )

    return <QuestionThreadsContext.Provider value={value}>{children}</QuestionThreadsContext.Provider>
}
